import React, { useMemo, useState, useCallback } from 'react'
import { Table, Button, notification, Flex, Tooltip, Pagination } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Icon } from '@/shared/ui/Icon/Icon'
import { Badge } from '@/shared/ui/components/Badge'
import { theme } from '@/app/styles/theme'
import {
  PAGINATION_OPTIONS,
  TASK_TABS,
  TaskStatusType,
} from '../../lib/taskTypes'
import { TASK_STATUSES, TaskItem } from '@/entities/task/model/taskTypes'
import { Content, TaskModal } from './styled'
import { LogsModal } from '../LogsModal'
import { TaskCard } from '../TaskCard'
import { PagerButton, PaginationContainer } from '../../styled'
import { ReconfigurationModal } from '../ReconfigurationModal'
import { TaskFilterTabs } from './ui/TaskFilterTabs'

interface AllTasksProps {
  tasks: TaskItem[]
  allTasksFull?: TaskItem[]
  isLoading: boolean
  onDeleteTask: (taskId: string) => Promise<void>
  onDownloadCredentials?: (taskId: string) => Promise<void>
  onSuspendTask?: (taskId: string) => Promise<void>
  onResumeTask?: (taskId: string) => Promise<void>
  deletingTasks: Set<string>
  refreshingTasks?: Set<string>
  onToggleLogs?: (taskId: string) => void
  activeFilter: TaskStatusType
  onFilterChange: (filter: TaskStatusType) => void
  currentUsername?: string | null
}

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'successed':
    case 'completed':
    case 'finished':
      return theme.colors.success
    case 'processing':
    case 'in progress':
    case 'running':
    case 'started':
      return theme.colors.tag.processed
    case 'failed':
    case 'error':
      return theme.colors.error
    case 'paused':
    case 'suspended':
      return theme.colors.tag.warning
    case 'created':
    case 'pending':
      return theme.colors.tag.created
    case 'deleted':
      return theme.colors.checkbox.border
    default:
      return 'default'
  }
}

export type { TaskStatusType } from '../../lib/taskTypes'

export const AllTasks: React.FC<AllTasksProps> = ({
  tasks,
  allTasksFull,
  isLoading,
  onDeleteTask,
  onDownloadCredentials,
  onSuspendTask,
  onResumeTask,
  deletingTasks,
  refreshingTasks,
  onToggleLogs,
  activeFilter,
  onFilterChange,
  currentUsername,
}) => {
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string>('')
  const [selectedTaskNumber, setSelectedTaskNumber] = useState<number>(0)

  const [taskDetailsModalVisible, setTaskDetailsModalVisible] = useState(false)
  const [selectedTaskForDetails, setSelectedTaskForDetails] =
    useState<TaskItem | null>(null)

  const [reconfigModalVisible, setReconfigModalVisible] = useState(false)
  const [selectedTaskForReconfig, setSelectedTaskForReconfig] =
    useState<TaskItem | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(
    PAGINATION_OPTIONS.ALL_TASKS_DEFAULT_SIZE
  )

  const tasksForTable = useMemo(() => {
    if (activeFilter === TASK_TABS.ALL) {
      return tasks
    }

    return tasks.filter((task) => {
      const status = task.status.toLowerCase()
      switch (activeFilter) {
        case 'processing':
          return (
            status === 'processing' ||
            status === 'in progress' ||
            status === 'running' ||
            status === 'created' ||
            status === 'started' ||
            status === 'pending'
          )
        case 'completed':
          return (
            status === 'successed' ||
            status === 'completed' ||
            status === 'finished'
          )
        case 'failed':
          return status === 'failed' || status === 'error'
        case 'suspended':
          return status === 'suspended'
        case 'deleted':
          return status === 'deleted'
        default:
          return true
      }
    })
  }, [tasks, activeFilter])

  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return tasksForTable.slice(startIndex, endIndex)
  }, [tasksForTable, currentPage, pageSize])

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page)
    if (newPageSize) {
      setPageSize(newPageSize)
    }
  }

  const tasksForCounts = allTasksFull || tasks

  const taskCounts = useMemo(() => {
    const counts = {
      all: tasksForCounts.length,
      processing: 0,
      completed: 0,
      suspended: 0,
      failed: 0,
      deleted: 0,
    }

    tasksForCounts.forEach((task) => {
      const status = task.status.toLowerCase()
      if (status === 'deleted') {
        counts.deleted++
      } else if (
        status === 'successed' ||
        status === 'completed' ||
        status === 'finished'
      ) {
        counts.completed++
      } else if (status === 'failed' || status === 'error') {
        counts.failed++
      } else if (status === 'suspended') {
        counts.suspended++
      } else if (
        status === 'processing' ||
        status === 'in progress' ||
        status === 'running' ||
        status === 'created' ||
        status === 'started'
      ) {
        counts.processing++
      }
    })

    return counts
  }, [tasksForCounts])

  const selectedTask = useMemo(() => {
    return tasks.find((task) => task.id === selectedTaskId)
  }, [tasks, selectedTaskId])

  const handleRowClick = useCallback((record: TaskItem) => {
    setSelectedTaskForDetails(record)
    setTaskDetailsModalVisible(true)
  }, [])

  const handleCloseTaskDetailsModal = useCallback(() => {
    setTaskDetailsModalVisible(false)
    setSelectedTaskForDetails(null)
  }, [])

  const handleToggleLogs = useCallback(
    (taskId: string) => {
      const task = tasks.find((t) => t.id === taskId)
      if (!task) return

      if (modalVisible && selectedTaskId === taskId) {
        setModalVisible(false)
        setSelectedTaskId('')
        return
      }

      if (modalVisible) {
        setModalVisible(false)
        setTimeout(() => {
          setSelectedTaskId(taskId)
          setSelectedTaskNumber(task.number)
          setModalVisible(true)
        }, 300)
      } else {
        setSelectedTaskId(taskId)
        setSelectedTaskNumber(task.number)
        setModalVisible(true)
      }

      onToggleLogs?.(taskId)
    },
    [tasks, modalVisible, selectedTaskId, onToggleLogs]
  )

  const handleDeleteTask = async (taskId: string) => {
    try {
      await onDeleteTask(taskId)
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось удалить задачу',
      })
    }
  }

  const handleReconfigClick = useCallback(
    (record: TaskItem, e: React.MouseEvent) => {
      e.stopPropagation()
      setSelectedTaskForReconfig(record)
      setReconfigModalVisible(true)
    },
    []
  )

  const handleCloseReconfigModal = useCallback(() => {
    setReconfigModalVisible(false)
    setSelectedTaskForReconfig(null)
  }, [])

  const hasVehiclesForReconfig = useCallback((task: TaskItem) => {
    return (task.stats?.Vehicles?.total || 0) > 0
  }, [])

  const isMyTask = useCallback(
    (task: TaskItem) => {
      return task.uploadAccount === currentUsername
    },
    [currentUsername]
  )

  const tableColumns: ColumnsType<TaskItem> = [
    {
      title: '№',
      dataIndex: 'number',
      key: 'number',
      width: '5%',
      defaultSortOrder: 'descend',
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="table_status" size={16} />
        </Flex>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 40,
      className: 'cell-button',
      align: 'center',
      render: (status: string) => {
        const color = getStatusColor(status)
        return <Badge color={color} />
      },
    },
    {
      title: 'Логин экспорта',
      dataIndex: 'uploadAccount',
      key: 'uploadAccount',
      width: '15%',
      ellipsis: true,
    },
    {
      title: 'Логин импорта',
      dataIndex: 'downloadLogin',
      key: 'downloadLogin',
      width: '15%',
      ellipsis: true,
    },
    {
      title: 'Клиент назначения',
      dataIndex: 'downloadAccount',
      key: 'destinationClient',
      width: '15%',
      ellipsis: true,
      render: (value: string) => value || 'Не указан',
    },
    {
      title: 'Дата начала импорта',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '15%',
      render: (date: string) => date || 'Не указана',
    },
    {
      title: 'Дата завершения',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: '15%',
      render: (date: string) => date || 'Не завершена',
    },
    {
      title: 'Прогресс, %',
      dataIndex: 'progress',
      key: 'progress',
      width: '15%',
      render: (progress: number) => (
        <span>{progress.toFixed(1).replace('.', ',')}</span>
      ),
    },
    {
      title: 'Период, дни ',
      key: 'period',
      dataIndex: 'period',
      width: '15%',
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="table_add" size={16} />
        </Flex>
      ),
      key: 'add',
      align: 'center',
      width: 40,
      className: 'cell-button',
      hidden: true,
      render: (_, record) => {
        const isDeleted = record.status === TASK_STATUSES.DELETED

        if (isDeleted) {
          return null
        }

        return (
          <Tooltip title="Увеличение периода">
            <Button
              type="text"
              icon={<Icon name="table_add" size={16} />}
              // onClick={() => onDownloadCredentials(record.id)}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="table_users" size={16} />
        </Flex>
      ),
      key: 'credentials',
      className: 'cell-button',
      align: 'center',
      width: 40,
      render: (_, record) => {
        const isDeleted = record.status === TASK_STATUSES.DELETED

        if (isDeleted || !onDownloadCredentials) {
          return null
        }

        return (
          <Tooltip title="Скачать данные пользователей">
            <Button
              type="text"
              icon={<Icon name="table_users" size={16} />}
              onClick={(e) => {
                e.stopPropagation()
                onDownloadCredentials(record.id)
              }}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="table_logs" size={16} />
        </Flex>
      ),
      key: 'logs',
      align: 'center',
      className: 'cell-button',
      width: 40,
      render: (_, record) => {
        return (
          <Tooltip title="Показать логи">
            <Button
              type="text"
              icon={<Icon name="table_logs" size={16} />}
              onClick={(e) => {
                e.stopPropagation()
                handleToggleLogs(record.id)
              }}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="table_pause" size={16} />
        </Flex>
      ),
      key: 'suspend',
      className: 'cell-button',
      align: 'center',
      width: 40,
      render: (_, record) => {
        const isDeleted = record.status === TASK_STATUSES.DELETED
        const isSuspended = record.status === TASK_STATUSES.SUSPENDED
        const canBeSuspended =
          record.status === TASK_STATUSES.STARTED ||
          record.status === TASK_STATUSES.CREATED
        const isLoading = refreshingTasks?.has(record.id)

        if (isDeleted || (!isSuspended && !canBeSuspended)) {
          return null
        }

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          if (isLoading) return

          if (isSuspended && onResumeTask) {
            onResumeTask(record.id)
          } else if (!isSuspended && onSuspendTask) {
            onSuspendTask(record.id)
          }
        }

        if (isLoading) {
          return (
            <Tooltip title="Обновление...">
              <Button
                type="text"
                icon={<Icon name="spinner" size={16} spin />}
                size="small"
                disabled
              />
            </Tooltip>
          )
        }

        if (isSuspended) {
          return (
            <Tooltip title="Запустить задачу">
              <Button
                type="text"
                icon={<Icon name="table_play" size={16} />}
                onClick={handleClick}
                size="small"
              />
            </Tooltip>
          )
        }

        return (
          <Tooltip title="Приостановить задачу">
            <Button
              type="text"
              icon={<Icon name="table_pause" size={16} />}
              onClick={handleClick}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Flex justify="center">
          <Icon name="reconfig" size={16} fill="#525252" />
        </Flex>
      ),
      key: 'reconfig',
      align: 'center',
      className: 'cell-button',
      width: 40,
      render: (_, record) => {
        const isDeleted = record.status === TASK_STATUSES.DELETED
        const isSuccessed = record.status === TASK_STATUSES.SUCCESSED
        const hasVehicles = hasVehiclesForReconfig(record)
        const isOwnTask = isMyTask(record)

        if (isDeleted || !isSuccessed || !hasVehicles || !isOwnTask) {
          return null
        }

        return (
          <Tooltip title="Перенастройка оборудования">
            <Button
              type="text"
              icon={<Icon name="reconfig" size={16} fill="525252" />}
              onClick={(e) => handleReconfigClick(record, e)}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Flex justify="center">
          <Icon
            name="table_delete"
            size={16}
            fill={theme.colors.cardDescription}
          />
        </Flex>
      ),
      key: 'delete',
      align: 'center',
      className: 'cell-button',
      width: 40,
      render: (_, record) => {
        const isDeleted = record.status === TASK_STATUSES.DELETED

        if (isDeleted) {
          return null
        }

        return (
          <Tooltip title="Удалить задачу">
            <Button
              type="text"
              icon={
                <Icon name="table_delete" size={16} fill={theme.colors.error} />
              }
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteTask(record.id)
              }}
              loading={deletingTasks.has(record.id)}
              danger
              size="small"
            />
          </Tooltip>
        )
      },
    },
  ]

  const handleFilterChange = (filter: TaskStatusType) => {
    onFilterChange(filter)
    setCurrentPage(1)
  }

  const rowProps = useCallback(
    (record: TaskItem) => ({
      onClick: () => handleRowClick(record),
      style: {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
      onMouseEnter: (e: React.MouseEvent<HTMLTableRowElement>) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5'
      },
      onMouseLeave: (e: React.MouseEvent<HTMLTableRowElement>) => {
        e.currentTarget.style.backgroundColor = ''
      },
    }),
    [handleRowClick]
  )

  return (
    <>
      <Content vertical>
        <TaskFilterTabs
          activeFilter={activeFilter}
          taskCounts={taskCounts}
          onChange={handleFilterChange}
        />
        <Table
          bordered
          dataSource={paginatedTasks}
          columns={tableColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 1300, y: 'max-content' }}
          loading={isLoading}
          onRow={rowProps}
        />
        {tasksForTable.length > 0 && (
          <PaginationContainer justify="end" style={{ marginTop: 16 }}>
            <Pagination
              current={currentPage}
              total={tasksForTable.length}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={[...PAGINATION_OPTIONS.PAGE_ALL_SIZE_OPTIONS]}
              showTotal={(total, range) => (
                <>
                  <span>Записей:</span> {range[0]}-{range[1]} из <b>{total}</b>
                </>
              )}
              itemRender={(
                _: any,
                type: string,
                originalElement: React.ReactNode
              ) => {
                if (type === 'prev') {
                  return (
                    <PagerButton gap={4} align="center">
                      <Icon name="pager_left" size={16} />
                    </PagerButton>
                  )
                }
                if (type === 'next') {
                  return (
                    <PagerButton gap={4} align="center">
                      <Icon name="pager_right" size={16} />
                    </PagerButton>
                  )
                }
                return originalElement
              }}
              locale={{
                items_per_page: '',
              }}
            />
          </PaginationContainer>
        )}
        <TaskModal
          open={taskDetailsModalVisible}
          onCancel={handleCloseTaskDetailsModal}
          footer={null}
          width={1248}
          centered
          closeIcon={<Icon name="closed_modal" size={16} />}
        >
          {selectedTaskForDetails && (
            <TaskCard
              task={selectedTaskForDetails}
              showLogs={false}
              onToggleLogs={() => {}}
              onDelete={async () => {
                await onDeleteTask(selectedTaskForDetails.id)
                handleCloseTaskDetailsModal()
              }}
              onDownloadCredentials={async () => {
                if (onDownloadCredentials) {
                  await onDownloadCredentials(selectedTaskForDetails.id)
                }
              }}
              isDeleting={deletingTasks.has(selectedTaskForDetails.id)}
              isModalView
            />
          )}
        </TaskModal>
        <LogsModal
          visible={modalVisible}
          taskId={selectedTaskId}
          taskNumber={selectedTaskNumber}
          taskStatus={selectedTask?.status || ''}
          onClose={() => {
            setModalVisible(false)
            setSelectedTaskId('')
          }}
        />
      </Content>

      <ReconfigurationModal
        open={reconfigModalVisible}
        onClose={handleCloseReconfigModal}
        taskNumber={selectedTaskForReconfig?.number}
        vehicleIds={selectedTaskForReconfig?.vehicleIds}
      />
    </>
  )
}
